<template>
  <div>
    <Header />
    <FlashMessage ref="flashMessage" />

    <div class="cart-page">
      <LoadingIndicator v-if="loading" message="Loading Cart..." />

      <h1>Your Cart</h1>
      <div class="cart-container">
        <!-- Check if cartItems has any items -->
        <div v-if="cartItems.length > 0" class="cart-items">
          <!-- Cart Items -->
          <div class="cart-item" v-for="item in cartItems" :key="item.id">
            <img
              :src="getFullImageUrl(item.product.thumbnail)"
              :alt="item.product.name"
              class="item-image"
            />
            <div class="item-details">
              <h3>{{ item.product.name }}</h3>
              <p class="price">Tsh: {{ item.product.price }}</p>
              
              <div class="quantity-controls">
                <button @click="decreaseQuantity(item)">-</button>
                <span>{{ item.quantity }}</span>
                <button @click="increaseQuantity(item)">+</button>
              </div>
              <p class="total">
                Total: Tsh:
                {{ (item.product.price * item.quantity).toFixed(2) }}
              </p>
              <button
                class="remove-btn"
                @click="RemoveFromCart(item.product.id, item)"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <!-- If cart is empty, show this message -->
        <div v-else class="empty-cart-message">
          <p>Your cart is currently empty. Start shopping now!</p>
          <!-- Add button to go to the shop -->
          <button class="go-to-shop-btn1" @click="goToShop">Go to Shop</button>
        </div>

        <!-- Order Summary (only visible if there are items in the cart) -->
        <div v-if="cartItems.length > 0" class="order-summary">
          <h2>Order Summary</h2>
          <div class="summary-details">
            <p>
              Subtotal: <span>Tsh: {{ subtotal.toFixed(2) }}</span>
            </p>
            <p>
              Shipping: <span>Tsh: {{ shippingCost.toFixed(2) }}</span>
            </p>
            <p>
              Taxes: <span>Tsh: {{ taxes.toFixed(2) }}</span>
            </p>
            <p class="total">
              Total: <span>Tsh: {{ totalAmount.toFixed(2) }}</span>
            </p>
          </div>

          <!-- Privacy Policy Agreement -->
          <div class="privacy-policy">
            <input
              type="checkbox"
              id="privacy-policy"
              v-model="isPrivacyPolicyAccepted"
            />
            <label for="privacy-policy">
              I agree to the <a href="#">Privacy Policy</a>
            </label>
          </div>

          <!-- Checkout Button -->
          <button
            class="checkout-btn"
            :disabled="!isPrivacyPolicyAccepted"
            @click="proceedToCheckout"
          >
            Proceed to Checkout
          </button>

          <!-- Go to Shop Button -->
          <button class="go-to-shop-btn" @click="goToShop">
            Continue Shopping
          </button>

          <!-- Promo Code Section -->
          <div class="promo-code">
            <input
              type="text"
              placeholder="Enter promo code"
              v-model="promoCode"
            />
            <button @click="applyPromoCode">Apply</button>
          </div>



        <div class="payment-methods">
          <h3>Supported Payment Methods</h3>

          <div class="payment-grid">
            <div class="payment-item">
              <img :src="mobileImg" alt="Mobile Payment" />
              <!-- <span>Mobile Money</span> -->
            </div>
          </div>
        </div>



        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Header from "../components/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import mobileImg from '@/assets/img/mobile_transfer.jpeg';


import {
  GetCartData,
  API_BASE_URL,
  RemoveProductFromCart,
  ReduceAddCartItemsNumber,
} from "../API-requests/Requests";

const router = useRouter();

const cartItems = ref([]);
const loading = ref(true);
const promoCode = ref("");

const isPrivacyPolicyAccepted = ref(false);
const error = ref(null);
const flashMessage = ref(null);
const QuantitToUpdate = ref(0);

// Cart Calculations
const subtotal = computed(() => {
  return cartItems.value.reduce(
    (total, item) => total + parseFloat(item.product.price) * item.quantity,
    0
  );
});

const shippingCost = computed(() => {
    return cartItems.value.reduce(
    (total, item) => total + parseFloat(item.product.delivery_fee) * item.quantity,
    0
  );
});

const taxes = computed(() => {
  return subtotal.value * 0.18; // 18% tax
});

const totalAmount = computed(() => {
  return subtotal.value + shippingCost.value + taxes.value;
});

// Cart Actions
const increaseQuantity = async (item) => {
  QuantitToUpdate.value = item.quantity + 1;
  item.quantity++;
  if (item.quantity > 0) {
    await UpdateCartItems(item.product.id, QuantitToUpdate.value);
  }
};

const decreaseQuantity = async (item) => {
  if (item.quantity > 1) {
    QuantitToUpdate.value = item.quantity - 1;
    item.quantity--;
    await UpdateCartItems(item.product.id, QuantitToUpdate.value);
  }
};

const removeItem = (item) => {
  cartItems.value = cartItems.value.filter(
    (cartItem) => cartItem.id !== item.id
  );
};

const RemoveFromCart = async (product_id, item) => {
  try {
    const response = await RemoveProductFromCart(product_id);
    flashMessage.value.showFlashMessage(
      "success",
      "Product removed from cart!"
    );

    removeItem(item);
  } catch (err) {
    console.error("Error remving product from cart:", err);
    error.value = "Error removing product from cart.";
    flashMessage.value.showFlashMessage("failure", error.value);
  }
  // finally {
  //   loading.value = false;
  // }
};

const applyPromoCode = () => {
  alert(`Promo code "${promoCode.value}" applied!`);
};

// Checkout Functionality
const proceedToCheckout = () => {
  if (isPrivacyPolicyAccepted.value) {
    router.push("/checkout");
  } else {
    flashMessage.value.showFlashMessage(
      "failure",
      "Please, agree to our privancy policy"
    );
  }
};

const goToShop = () => {
  router.push("/product");
};

onMounted(async () => {
  try {
    const response = await GetCartData();
    cartItems.value = response.cart_items;
  } catch (err) {
    console.error("Error fetching cart data:", err);
    alert("Failed to load cart data. Please try again later.");
  } finally {
    loading.value = false;
  }
});

const UpdateCartItems = async (product_id, QuantitToUpdate) => {
  try {
    if (QuantitToUpdate > 0) {
      const response = await ReduceAddCartItemsNumber(
        product_id,
        QuantitToUpdate
      );
    } else {
      console.error("Invalid quantity:", QuantitToUpdate);
      flashMessage.value.showFlashMessage("failure", "Invalid quantity.");
    }
  } catch (err) {
    console.error("Error updating cart:", err);
    error.value = "Error updating your cart.";
    flashMessage.value.showFlashMessage("failure", error.value);
  } finally {
    loading.value = false;
  }
};

const getFullImageUrl = (imagePath) => {
  return `${API_BASE_URL}${imagePath}`;
};
</script>

<style scoped>
/* General Styles */
.cart-page {
  padding: 10px;
  max-width: 1200px;
  margin: 0 auto;
}

.cart-page h1 {
  font-size: 22px;
  margin-bottom: 20px;
}

.cart-container {
  display: flex;
  gap: 20px;
}

/* Cart Items */
.cart-items {
  flex: 2;
}

.cart-item {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 20px;
}

.item-image {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
}

.item-details {
  flex: 1;
}

.item-details h3 {
  font-size: 1.2rem;
  margin-bottom: 10px;
}

.price {
  font-size: 1rem;
  color: #42b983;
  margin-bottom: 10px;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.quantity-controls button {
  padding: 5px 10px;
  font-size: 1rem;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.quantity-controls button:hover {
  background-color: #369f6e;
}

.total {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.remove-btn {
  padding: 5px 10px;
  font-size: 1rem;
  background-color: #ff4d4d;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.remove-btn:hover {
  background-color: #cc0000;
}

/* Order Summary */
.order-summary {
  flex: 1;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f8f9fa;
}

.order-summary h2 {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.summary-details p {
  font-size: 1rem;
  margin-bottom: 10px;
}

.summary-details .total {
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 20px;
}

/* Privacy Policy Agreement */
.privacy-policy {
  margin-top: 20px;
}

.privacy-policy label {
  font-size: 0.9rem;
}

.privacy-policy a {
  color: #007bff;
  text-decoration: none;
}

.privacy-policy a:hover {
  text-decoration: underline;
}

/* Checkout Button */
.checkout-btn {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 20px;
}

.checkout-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.checkout-btn:hover:not(:disabled) {
  background-color: #369f6e;
}

/* Go to Shop Button */
.go-to-shop-btn {
  width: 100%;
  padding: 10px;
  font-size: 1rem;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.go-to-shop-btn:hover {
  background-color: #0056b3;
}

.go-to-shop-btn1 {
  width: 50%;
  padding: 10px;
  font-size: 1rem;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.go-to-shop-btn1:hover {
  background-color: #0056b3;
}

/* Promo Code Section */
.promo-code {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.promo-code input {
  flex: 1;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.promo-code button {
  padding: 10px 20px;
  font-size: 1rem;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.promo-code button:hover {
  background-color: #0056b3;
}

/* Related Products */
.related-products {
  margin-top: 40px;
}

.related-products h2 {
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.product-card {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.empty-cart-message {
  text-align: center;
  font-size: 18px;
  margin-top: 50px;
  color: #555;
  flex: 2;
}

.product-card img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.product-card h3 {
  font-size: 1.2rem;
  margin: 10px 0;
}

.product-card .price {
  font-size: 1rem;
  color: #42b983;
  margin-bottom: 10px;
}

.add-to-cart-btn {
  padding: 10px 20px;
  font-size: 1rem;
  background-color: #42b983;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.add-to-cart-btn:hover {
  background-color: #369f6e;
}

/* Responsive Design */
@media (max-width: 768px) {
  .cart-container {
    flex-direction: column;
  }

  .cart-item {
    flex-direction: column;
    text-align: center;
  }

  .item-image {
    width: 100%;
    height: auto;
  }
}


/* css for mobile payments thumbnail */
.payment-methods {
  margin-top: 20px;
}

.payment-methods h3 {
  margin-bottom: 15px;
  font-size: 1.2rem;
}

.payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
}

.payment-item {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 10px;
  text-align: center;
  transition: 0.3s ease;
}

.payment-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.payment-item img {
  width: 100%;
  /* max-width: 80px; */
  height: auto;
  object-fit: contain;
  margin-bottom: 8px;
}

.payment-item span {
  font-size: 0.9rem;
  color: #333;
}
</style>
